import { useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap'
import { useMutation, useQuery } from 'react-query'
import { toast } from 'react-toastify'

import api from '../../services/api'
import {
  ESTILO_RESULTADO,
  ESTILO_SEVERIDAD,
  bloqueaTimbrado,
  claseDeHallazgo,
  describirValor,
  esEstadoSinValor,
  ordenarParaLeer,
  resumirHallazgos,
} from '../../utils/hallazgosNomina'

/**
 * Auditoría de nómina: recalcular una nómina ajena y ver en qué difiere.
 *
 * Es el modo que el requerimiento llama **ONLY READ**: no modifica la nómina analizada, la
 * reproduce con las reglas del ruleset y compara. Vendible sin timbrar un solo CFDI.
 *
 * La lógica de cómo se lee el informe vive en `utils/hallazgosNomina.js`, no aquí: dentro del
 * render no se puede probar, y el linter además prohíbe exportar funciones junto a un
 * componente.
 */
export default function AuditoriaNomina() {
  const [lote, setLote] = useState('')
  const [sistema, setSistema] = useState('GENERIC_IMPORT')
  const [texto, setTexto] = useState('')
  const [borrador, setBorrador] = useState(null)
  const [corrida, setCorrida] = useState(null)
  const [informe, setInforme] = useState(null)

  const { data: lotes, isLoading: cargandoLotes } = useQuery(
    'nomina-lotes',
    () => api.get('/payroll/v1/batches/').then((r) => r.data.results ?? r.data),
    { onError: () => toast.error('No se pudieron cargar los lotes de nómina') },
  )

  const importar = useMutation(
    ({ loteId, payload }) =>
      api.post(`/payroll/v1/batches/${loteId}/imports/`, { sistema, payload, relacion: 0 })
        .then((r) => r.data),
    {
      onSuccess: (data) => {
        setBorrador(data)
        setInforme(null)
      },
      onError: (e) =>
        toast.error(e?.response?.data?.detail || 'No se pudo leer el archivo'),
    },
  )

  const auditar = useMutation(
    async (runId) => {
      await api.post(`/payroll/v1/runs/${runId}/audit/`, {})
      return api.get(`/payroll/v1/runs/${runId}/findings/`).then((r) => r.data)
    },
    {
      onSuccess: setInforme,
      onError: (e) =>
        toast.error(e?.response?.data?.detail || 'No se pudo auditar la corrida'),
    },
  )

  const onImportar = (e) => {
    e.preventDefault()
    if (!lote) return toast.warn('Elige el lote al que pertenece esta nómina')
    let payload
    try {
      payload = JSON.parse(texto)
    } catch {
      return toast.error('El contenido no es JSON válido')
    }
    importar.mutate({ loteId: lote, payload })
  }

  const resumen = informe ? resumirHallazgos(informe.findings) : null
  const estilo = informe ? ESTILO_RESULTADO[informe.outcome] : null

  return (
    <Container fluid className="px-0">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">Auditoría de nómina</h4>
          <p className="text-muted mb-0">
            Recalcula una nómina con las reglas vigentes y señala en qué difiere.
            <strong> No modifica la nómina analizada.</strong>
          </p>
        </div>
      </div>

      <Row className="g-3">
        <Col lg={5}>
          <Card>
            <Card.Header>1. La nómina a revisar</Card.Header>
            <Card.Body>
              <Form onSubmit={onImportar}>
                <Form.Group className="mb-3">
                  <Form.Label>Lote</Form.Label>
                  <Form.Select value={lote} onChange={(e) => setLote(e.target.value)}
                               disabled={cargandoLotes}>
                    <option value="">Elige un lote…</option>
                    {(lotes || []).map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.jurisdiccion} · {l.fecha_efectiva} · {l.estado}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Sistema de origen</Form.Label>
                  <Form.Select value={sistema} onChange={(e) => setSistema(e.target.value)}>
                    <option value="GENERIC_IMPORT">Genérico</option>
                    <option value="NOI">NOI</option>
                    <option value="NOMIPAQ">Nomipaq</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Los campos significan cosas distintas en cada sistema, así que el mapeo
                    es por sistema. Hoy sólo el genérico tiene mapeos cargados.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Contenido</Form.Label>
                  <Form.Control as="textarea" rows={8} value={texto}
                                placeholder={'{\n  "employee": { "employee_id": "E-1" },\n  "earning": { "gross_amount": "12345.67" }\n}'}
                                onChange={(e) => setTexto(e.target.value)} />
                </Form.Group>

                <Button type="submit" disabled={importar.isLoading || !texto.trim()}>
                  {importar.isLoading ? <Spinner size="sm" animation="border" /> : 'Leer la nómina'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={7}>
          {borrador && <Borrador datos={borrador} />}

          {borrador && (
            <Card className="mt-3">
              <Card.Header>2. Auditar</Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Corrida ya calculada</Form.Label>
                  <Form.Control value={corrida || ''} placeholder="UUID de la corrida"
                                onChange={(e) => setCorrida(e.target.value)} />
                  <Form.Text className="text-muted">
                    La auditoría recalcula desde los hechos congelados y compara contra lo
                    que la corrida registró.
                  </Form.Text>
                </Form.Group>
                <Button variant="primary" disabled={!corrida || auditar.isLoading}
                        onClick={() => auditar.mutate(corrida)}>
                  {auditar.isLoading ? <Spinner size="sm" animation="border" /> : 'Auditar'}
                </Button>
              </Card.Body>
            </Card>
          )}

          {informe && (
            <Card className="mt-3">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span>3. Informe</span>
                <Badge bg={estilo?.variant || 'secondary'}>{estilo?.texto || informe.outcome}</Badge>
              </Card.Header>
              <Card.Body>
                {bloqueaTimbrado(informe) && (
                  <Alert variant="warning" className="py-2">
                    Este resultado <strong>impide emitir el CFDI</strong> hasta resolverse.
                  </Alert>
                )}

                <Row className="text-center mb-3">
                  <Col>
                    <div className="h4 mb-0 text-danger">{resumen.ERROR_DETECTADO}</div>
                    <small className="text-muted">errores detectados</small>
                  </Col>
                  <Col>
                    <div className="h4 mb-0 text-warning">{resumen.REQUIERE_REVISION}</div>
                    <small className="text-muted">requieren revisión</small>
                  </Col>
                  <Col>
                    <div className="h4 mb-0 text-danger">{resumen.CONFLICTO}</div>
                    <small className="text-muted">conflictos normativos</small>
                  </Col>
                </Row>

                <Alert variant="light" className="py-2 small">
                  Un <strong>error detectado</strong> es un número que no cuadra. Uno que
                  <strong> requiere revisión</strong> es un dato que falta: nadie calculó mal,
                  falta información para poder afirmar.
                </Alert>

                <Hallazgos lista={informe.findings} />
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  )
}

/** Lo que la ingesta leyó, antes de calcular nada. */
function Borrador({ datos }) {
  const campos = Object.entries(datos.payload_canonico || {})
  const sinValor = campos.filter(([, v]) => esEstadoSinValor(v))

  return (
    <Card>
      <Card.Header>Lo que se leyó del archivo</Card.Header>
      <Card.Body>
        {sinValor.length > 0 && (
          <Alert variant="info" className="py-2 small">
            <strong>{sinValor.length} dato(s) sin valor utilizable.</strong> Un cero que la
            fuente no declara como real se lee «no se sabe»: puede ser que el trabajador no
            tuviera ese concepto, o que el sistema de origen no lo exporte.
          </Alert>
        )}
        {datos.sin_mapeo?.length > 0 && (
          <Alert variant="warning" className="py-2 small">
            <strong>{datos.sin_mapeo.length} campo(s) sin mapeo:</strong>{' '}
            {datos.sin_mapeo.join(', ')}. No se interpretan por su nombre.
          </Alert>
        )}
        <Table size="sm" responsive className="mb-0">
          <thead><tr><th>Campo canónico</th><th>Valor</th><th>Origen</th></tr></thead>
          <tbody>
            {campos.map(([campo, valor]) => (
              <tr key={campo}>
                <td><code className="small">{campo}</code></td>
                <td className={esEstadoSinValor(valor) ? 'text-muted fst-italic' : ''}>
                  {describirValor(valor)}
                </td>
                <td className="small text-muted">
                  {datos.procedencia?.[campo]?.campo || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  )
}

/** Los hallazgos, con lo que bloquea arriba. */
function Hallazgos({ lista }) {
  const ordenados = ordenarParaLeer(lista)
  if (!ordenados.length) {
    return <Alert variant="success" className="py-2 mb-0">
      El recálculo reprodujo la nómina sin diferencias.
    </Alert>
  }
  return (
    <Table size="sm" responsive className="mb-0">
      <thead>
        <tr><th>Qué</th><th>Regla</th><th>Esperado</th><th>Registrado</th><th>Fundamento</th></tr>
      </thead>
      <tbody>
        {ordenados.map((h, i) => (
          <tr key={`${h.code}-${h.rule_id}-${i}`}>
            <td>
              <Badge bg={ESTILO_SEVERIDAD[h.severity] || 'secondary'} className="me-1">
                {claseDeHallazgo(h) === 'ERROR_DETECTADO' ? 'Error' :
                 claseDeHallazgo(h) === 'CONFLICTO' ? 'Conflicto' : 'Revisar'}
              </Badge>
              <span className="small">{h.code}</span>
            </td>
            <td className="small"><code>{h.rule_id || '—'}</code></td>
            <td className="small">{describirValor(h.expected)}</td>
            <td className="small">{describirValor(h.actual)}</td>
            <td className="small text-muted">{(h.source_ids || []).join(', ') || '—'}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  )
}
